// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux (web, étape 2) — accès aux API des modules prêts, aides, finance, paie (simulation) et
// configuration. Routes exactes : backend/src/dispositifs/{prets,aides,finance,paie,configuration}/README.md.
// Réponses { success, data } ; listes paginées { elements, page, taille, total, suivant }.
// Tout POST financier ou de décision porte l'en-tête Idempotency-Key (UUID v4) : la clé est créée à l'ouverture de la
// fenêtre (FenetreActe) et réutilisée si la personne renvoie la même demande (réponse perdue → même réponse). Le serveur
// mémorise aussi les refus définitifs (404, 409…) : après une réponse 4xx, la fenêtre prend une nouvelle clé
// (docs/rh/securite/idempotence/MECANISME.md).
// Le serveur reste seul juge : toute erreur (403, 409…) est rendue telle qu'il l'envoie.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const PRETS = '/api/dispositifs/prets';
const AIDES = '/api/dispositifs/aides';
const FIN = '/api/dispositifs/finance';
const PAIE = '/api/dispositifs/paie';
const CONF = '/api/dispositifs/configuration';

/** UUID v4 (Idempotency-Key) ; repli sur getRandomValues hors contexte sécurisé, jamais sur un aléa non cryptographique. */
export function nouvelleCle() {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (!c || typeof c.getRandomValues !== 'function') throw new Error('Générateur aléatoire cryptographique indisponible dans ce navigateur.');
  const o = new Uint8Array(16);
  c.getRandomValues(o);
  o[6] = (o[6] & 0x0f) | 0x40; o[8] = (o[8] & 0x3f) | 0x80;
  const h = [...o].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * Règle commune de la clé d'idempotence après un échec (docs/rh/securite/idempotence/MECANISME.md § 3) : la clé est
 * RENOUVELÉE après toute réponse 4xx (le serveur peut avoir mémorisé ce refus), sauf 409 IDEMPOTENCE_EN_COURS ; elle est
 * GARDÉE après une coupure, un délai dépassé ou un 5xx (le renvoi reçoit la réponse d'origine).
 */
export function cleARenouveler(e) {
  const statut = e?.response?.status;
  return statut >= 400 && statut < 500 && e.response.data?.code !== 'IDEMPOTENCE_EN_COURS';
}

/** Route absente du serveur (404 sans code métier) : fonction pas encore livrée. */
export const routeAbsente = (e) => e?.response?.status === 404 && !(e.response.data && e.response.data.code);

function client(token) {
  const entetes = token ? { Authorization: `Bearer ${token}` } : {};
  const get = async (url, params) => (await axios.get(url, { params, headers: entetes })).data.data;
  const post = async (url, corps, cle) => (await axios.post(url, corps || {}, { headers: cle ? { ...entetes, 'Idempotency-Key': cle } : entetes })).data.data;
  const sansVide = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== '' && v !== null && v !== undefined));
  /** Première route présente parmi deux (paiements : routes publiées par FINANCE, puis noms annoncés par l'intégration). */
  const auChoix = async (a, b) => { try { return await a(); } catch (e) { if (routeAbsente(e)) return b(); throw e; } };
  const multipart = async (url, champs, cle) => {
    const f = new FormData();
    Object.entries(champs).forEach(([k, v]) => { if (v !== undefined && v !== null) f.append(k, v); });
    return (await axios.post(url, f, { headers: cle ? { ...entetes, 'Idempotency-Key': cle } : entetes })).data.data;
  };
  const blob = async (url) => {
    const r = await axios.get(url, { headers: entetes, responseType: 'blob' });
    return { blob: r.data, type: r.headers['content-type'] || r.data.type };
  };
  return {
    // Prêts
    prets: (org, { page = 1, taille = 25, statut, dispositif } = {}) => get(`${PRETS}/demandes`, sansVide({ organisation_id: org, page, taille, statut, dispositif_id: dispositif })),
    pret: (id) => get(`${PRETS}/demandes/${id}`),
    produitPret: (dispositifId) => get(`${PRETS}/produits/${dispositifId}`),
    simulerPret: (corps) => post(`${PRETS}/simulation`, corps),
    saisirPret: (corps, cle) => post(`${PRETS}/demandes`, corps, cle),
    instruirePret: (id, corps) => post(`${PRETS}/demandes/${id}/instruire`, corps),
    deciderPret: (id, corps, cle) => post(`${PRETS}/demandes/${id}/decider`, corps, cle),
    formalitesPret: (id, corps) => post(`${PRETS}/demandes/${id}/formalites`, corps),
    /** Versement par la trésorerie (DT-INTEG-005) : paiement PRET préparé ; l'ancienne route /decaisser répond 409. */
    preparerPaiementPret: (id, corps, cle) => post(`${PRETS}/demandes/${id}/paiement`, corps, cle),
    annulerPret: (id, motif) => post(`${PRETS}/demandes/${id}/annuler`, { motif }),
    proposerActePret: (id, corps, cle) => post(`${PRETS}/demandes/${id}/actes`, corps, cle),
    deciderActePret: (decisionId, corps, cle) => post(`${PRETS}/actes/${decisionId}/decider`, corps, cle),
    // Aides
    typesAide: (dispositifId, actifs = true) => get(`${AIDES}/dispositifs/${dispositifId}/types`, actifs ? { actifs: 'true' } : {}),
    aides: (org, { page = 1, taille = 25, statut, dispositif, exercice } = {}) => get(`${AIDES}/organisations/${org}/demandes`, sansVide({ page, taille, statut, dispositif, exercice })),
    syntheseAides: (org, exercice) => get(`${AIDES}/organisations/${org}/synthese`, sansVide({ exercice })),
    aide: (id) => get(`${AIDES}/demandes/${id}`),
    motifAide: (id) => get(`${AIDES}/demandes/${id}/motif`),
    deposerAide: (corps, cle) => post(`${AIDES}/demandes`, corps, cle),
    ajouterPieceAide: (id, { fichier, typePiece, libelle }) => multipart(`${AIDES}/demandes/${id}/pieces`, { type_piece: typePiece, libelle, fichier }),
    fichierPieceAide: (id, pieceId) => blob(`${AIDES}/demandes/${id}/pieces/${pieceId}/fichier`),
    verifierAide: (id, corps) => post(`${AIDES}/demandes/${id}/verifier`, corps),
    deciderAide: (id, corps, cle) => post(`${AIDES}/demandes/${id}/decider`, corps, cle),
    annulerAide: (id, motif, cle) => post(`${AIDES}/demandes/${id}/annuler`, { motif }, cle),
    ordrePaiementAide: (id) => get(`${AIDES}/demandes/${id}/ordre-paiement`),
    verserAide: (id, corps, cle) => post(`${AIDES}/demandes/${id}/verser`, corps, cle),
    // Finance : créances
    creances: (org, { page = 1, taille = 25, etat, dispositif } = {}) => get(`${FIN}/organisations/${org}/creances`, sansVide({ page, taille, etat, dispositif })),
    creance: (id) => get(`${FIN}/creances/${id}`),
    proposerMouvement: (creanceId, corps, cle) => post(`${FIN}/creances/${creanceId}/mouvements`, corps, cle),
    deciderMouvement: (id, corps, cle) => post(`${FIN}/mouvements/${id}/decider`, corps, cle),
    contrepasserMouvement: (id, motif, cle) => post(`${FIN}/mouvements/${id}/contrepasser`, { motif }, cle),
    // Finance : paiements (préparer → signer → contresigner → exécuter ; annulation motivée)
    paiements: (org, { page = 1, taille = 25, statut, sens } = {}) => auChoix(
      () => get(`${FIN}/organisations/${org}/paiements`, sansVide({ page, taille, statut, sens })),
      () => get(`${FIN}/paiements`, sansVide({ organisation_id: org, page, taille, statut, sens })),
    ),
    /** Détail : route rattachée à l'organisation (nommage publié), chemin court en repli. */
    paiement: (id, org) => (org ? auChoix(() => get(`${FIN}/organisations/${org}/paiements/${id}`), () => get(`${FIN}/paiements/${id}`)) : get(`${FIN}/paiements/${id}`)),
    preparerPaiement: (org, corps, cle) => post(`${FIN}/organisations/${org}/paiements`, corps, cle),
    signerPaiement: (id, cle) => post(`${FIN}/paiements/${id}/signer`, {}, cle),
    secondSignerPaiement: (id, cle) => post(`${FIN}/paiements/${id}/second-signer`, {}, cle),
    executerPaiement: (id, corps, cle) => post(`${FIN}/paiements/${id}/executer`, corps, cle),
    annulerPaiement: (id, motif, cle) => post(`${FIN}/paiements/${id}/annuler`, { motif }, cle),
    // Finance : trésorerie et comptabilité de démonstration
    tresorerie: (org) => get(`${FIN}/organisations/${org}/tresorerie`),
    etablirArrete: (compteId, corps, cle) => post(`${FIN}/comptes-tresorerie/${compteId}/arretes`, corps, cle),
    controlerArrete: (id, cle) => post(`${FIN}/arretes/${id}/controler`, {}, cle),
    etablirRapprochement: (compteId, corps, cle) => post(`${FIN}/comptes-tresorerie/${compteId}/rapprochements`, corps, cle),
    controlerRapprochement: (id, cle) => post(`${FIN}/rapprochements/${id}/controler`, {}, cle),
    // Chèques d'un compte (DM-WEB-002 n° 2), arrêtés et rapprochements avec identifiants (n° 3)
    cheques: (compteId, { statut, page = 1, taille = 50 } = {}) => get(`${FIN}/comptes-tresorerie/${compteId}/cheques`, sansVide({ statut, page, taille })),
    enregistrerChequier: (compteId, corps, cle) => post(`${FIN}/comptes-tresorerie/${compteId}/chequiers`, corps, cle),
    changerCheque: (id, corps, cle) => post(`${FIN}/cheques/${id}/statut`, corps, cle),
    arretes: (compteId, { page = 1, taille = 25 } = {}) => get(`${FIN}/comptes-tresorerie/${compteId}/arretes`, { page, taille }),
    rapprochements: (compteId, { page = 1, taille = 25 } = {}) => get(`${FIN}/comptes-tresorerie/${compteId}/rapprochements`, { page, taille }),
    comptabilite: (org) => get(`${FIN}/organisations/${org}/comptabilite`),
    ecritures: (org, { page = 1, taille = 25, journal, periode, statut } = {}) => get(`${FIN}/organisations/${org}/ecritures`, sansVide({ page, taille, journal, periode, statut })),
    validerEcriture: (id, cle) => post(`${FIN}/ecritures/${id}/valider`, {}, cle),
    contrepasserEcriture: (id, motif, cle) => post(`${FIN}/ecritures/${id}/contrepasser`, { motif }, cle),
    abandonnerEcriture: (id, motif, cle) => post(`${FIN}/ecritures/${id}/abandonner`, { motif }, cle),
    demanderCloture: (org, mois, cle) => post(`${FIN}/organisations/${org}/periodes/${mois}/cloture/demander`, {}, cle),
    confirmerCloture: (org, mois, corps, cle) => post(`${FIN}/organisations/${org}/periodes/${mois}/cloture/confirmer`, corps, cle),
    // Paie (mode vérification : lecture et simulation seulement)
    retenues: (periode, org, { page = 1, taille = 25, statut } = {}) => get(`${PAIE}/periodes/${periode}`, sansVide({ organisation: org, page, taille, statut })),
    retenue: (id) => get(`${PAIE}/evenements/${id}`),
    simulerRetenues: (corps, cle) => post(`${PAIE}/simulations`, corps, cle),
    simulationsRetenues: (periode, org) => get(`${PAIE}/simulations`, sansVide({ periode, organisation: org })),
    simulationRetenues: (id) => get(`${PAIE}/simulations/${id}`),
    // Fondements de retenue (PAIE, routes annoncées : POST /fondements, POST /fondements/:id/confirmer ; lecture tolérante).
    // Enregistrer un fondement n'ouvre AUCUN prélèvement : la transmission à la Paie reste fermée.
    fondements: (org, { page = 1, taille = 25, statut } = {}) => auChoix(
      () => get(`${PAIE}/fondements`, sansVide({ organisation: org, page, taille, statut })),
      () => get(`${PAIE}/organisations/${org}/fondements`, sansVide({ page, taille, statut })),
    ),
    enregistrerFondement: (corps, cle) => post(`${PAIE}/fondements`, corps, cle),
    confirmerFondement: (id, cle) => post(`${PAIE}/fondements/${id}/confirmer`, {}, cle),
    retirerFondement: (id, motif, cle) => post(`${PAIE}/fondements/${id}/retirer`, { motif }, cle),
    // Configuration (règlement du dispositif, simulation budgétaire)
    reglement: (dispositifId, date) => get(`${CONF}/reglements/${dispositifId}`, sansVide({ date })),
    gabarit: (code) => get(`${CONF}/gabarits/${code}`),
    simulerReglement: (dispositifId, hypotheses) => post(`${CONF}/reglements/${dispositifId}/simuler`, hypotheses ? { hypotheses } : {}),
  };
}

/** Client des modules sociaux pour le composant courant. */
export function useSocial() {
  const { token } = useAuth();
  return useMemo(() => client(token), [token]);
}

// ─── Mise en forme ─────────────────────────────────────────────────────────────────────────────
const ESPACE = ' ';
/** « 300 000 F » (entier FCFA, espaces insécables) ; absent → tiret long. */
export const francs = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const v = Math.trunc(Number(n));
  return `${v < 0 ? '−' : ''}${String(Math.abs(v)).replace(/\B(?=(\d{3})+(?!\d))/g, ESPACE)}${ESPACE}F`;
};
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
/** « 12 mars 2027 » (date AAAA-MM-JJ ou horodatage, fuseau de Dakar). */
export function dateLongue(d) {
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [a, m, j] = d.split('-').map(Number);
    return `${j === 1 ? '1er' : j} ${MOIS[m - 1]} ${a}`;
  }
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Dakar', year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(x).map((o) => [o.type, o.value]));
  return dateLongue(`${p.year}-${p.month}-${p.day}`);
}
/** « 12 mars 2027 à 10 h 05 » (Dakar). */
export function dateHeureLongue(d) {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const h = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Africa/Dakar', hour: '2-digit', minute: '2-digit' }).format(x).replace(':', ' h ');
  return `${dateLongue(d)} à ${h}`;
}
/** « mars 2027 » pour une période AAAA-MM. */
export const moisLong = (p) => (/^\d{4}-\d{2}$/.test(String(p || '')) ? `${MOIS[Number(p.slice(5, 7)) - 1]} ${p.slice(0, 4)}` : (p || ''));
/** Période courante (AAAA-MM, Dakar). */
export const periodeCourante = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Dakar', year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);
/** Période décalée de n mois. */
export function periodeDecalee(p, n) {
  const [a, m] = p.split('-').map(Number);
  const t = a * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
/** Saisie d'un montant (« 300 000 », « 300000 ») → entier, ou null. */
export const lireMontant = (s) => {
  const t = String(s ?? '').replace(/[\s  ]/g, '');
  return /^\d{1,12}$/.test(t) ? Number(t) : null;
};

// ─── Libellés (vocabulaire du gestionnaire) ────────────────────────────────────────────────────
export const ETATS_PRET = {
  BROUILLON: ['neutre', 'Brouillon'], DEPOSEE: ['info', 'Demande reçue'], EN_INSTRUCTION: ['info', 'À l\'étude'], COMPLEMENT_DEMANDE: ['alerte', 'Complément demandé'],
  IRRECEVABLE: ['neutre', 'Non recevable'], A_DECIDER: ['alerte', 'En attente de décision'], ACCORDEE: ['succes', 'Accordée'], REFUSEE: ['neutre', 'Non accordée'],
  FORMALITES: ['alerte', 'Formalités en cours'], A_DECAISSER: ['alerte', 'À verser (trésorerie)'], DECAISSEE: ['succes', 'Fonds versés'], DESISTEE: ['neutre', 'Retirée'],
  ANNULEE: ['neutre', 'Annulée'],
};
export const FILTRES_PRET = ['DEPOSEE', 'EN_INSTRUCTION', 'COMPLEMENT_DEMANDE', 'A_DECIDER', 'ACCORDEE', 'A_DECAISSER', 'DECAISSEE', 'REFUSEE', 'ANNULEE'];
export const ETATS_AIDE = {
  DEPOSEE: ['alerte', 'Pièces attendues'], PIECES_A_VERIFIER: ['info', 'Pièces à vérifier'], EN_INSTRUCTION: ['info', 'En instruction'],
  EN_ATTENTE_BUDGET: ['alerte', 'En attente de budget'], ACCORDEE: ['succes', 'Accordée, à verser'], REFUSEE: ['neutre', 'Refusée'], VERSEE: ['succes', 'Versée'], ANNULEE: ['neutre', 'Annulée'],
};
export const ETATS_CREANCE = {
  ACTIVE: ['info', 'En remboursement'], SUSPENDUE: ['alerte', 'Suspendue'], LITIGE: ['danger', 'En litige'], DIRECTE: ['info', 'Remboursement direct'],
  SOLDEE: ['succes', 'Soldée'], ABANDONNEE: ['neutre', 'Abandonnée'],
};
export const ETATS_ECHEANCIER = {
  PREVISIONNEL: ['neutre', 'Prévisionnel'], EN_VIGUEUR: ['succes', 'En vigueur'], REMPLACE: ['neutre', 'Remplacé'], ANNULE: ['neutre', 'Annulé'], CLOS: ['neutre', 'Clos'],
};
export const CAUSES_ECHEANCIER = {
  INITIAL: 'Échéancier initial', CONSTITUTION: 'Constitution de la créance', REMBOURSEMENT_ANTICIPE: 'Remboursement anticipé', SUSPENSION: 'Suspension',
  REPORT: 'Report', REECHELONNEMENT: 'Rééchelonnement', RELIQUAT: 'Reliquat', REGULARISATION: 'Régularisation',
};
export const ETATS_ECHEANCE = {
  A_VENIR: ['neutre', 'À venir'], PROPOSEE: ['info', 'Proposée à la Paie'], RETENUE: ['succes', 'Retenue'], PARTIELLE: ['alerte', 'Retenue partielle'],
  NON_RETENUE: ['danger', 'Non retenue'], REMBOURSEE_DIRECT: ['succes', 'Réglée directement'], ANNULEE: ['neutre', 'Annulée'],
};
export const NATURES_MOUVEMENT = {
  CONSTITUTION: 'Constitution', RETENUE_PAIE: 'Retenue sur salaire', REMBOURSEMENT_DIRECT: 'Remboursement direct', AVOIR: 'Avoir', ABANDON: 'Abandon de créance',
  REGULARISATION: 'Régularisation', CONTREPASSATION: 'Contrepassation',
};
export const STATUTS_MOUVEMENT = { A_VALIDER: ['alerte', 'À valider'], VALIDE: ['succes', 'Validé'], REJETE: ['neutre', 'Rejeté'] };
export const STATUTS_CHEQUE = {
  DISPONIBLE: ['succes', 'Disponible'], EMIS: ['info', 'Émis'], REMIS: ['info', 'Remis au bénéficiaire'], DEBITE: ['neutre', 'Débité'], ANNULE: ['neutre', 'Annulé'],
  PERDU_VOLE: ['danger', 'Perdu ou volé (opposition)'],
};
export const NATURES_FONDEMENT = { CESSION_GREFFE: 'Cession au greffe', REMBOURSEMENT_DIRECT: 'Remboursement direct (sans retenue)', AUTRE_VALIDEE_JURISTE: 'Autre fondement validé par le juriste' };
export const STATUTS_FONDEMENT = { A_CONFIRMER: ['alerte', 'À confirmer'], VALIDE: ['succes', 'Confirmé'], RETIRE: ['neutre', 'Retiré'] };
export const ETATS_PAIEMENT = { PREPARE: ['alerte', 'Préparé'], SIGNE: ['info', 'Signé'], EXECUTE: ['succes', 'Exécuté'], ANNULE: ['neutre', 'Annulé'] };
export const MODES_PAIEMENT = { VIREMENT: 'Virement', CHEQUE: 'Chèque', ESPECES: 'Espèces', MOBILE_MONEY: 'Mobile money', AUTRE: 'Autre' };
export const MODES_PRET = ['VIREMENT', 'CHEQUE', 'ESPECES', 'AUTRE'];
export const OBJETS_PAIEMENT = {
  PRET: 'Prêt social', AIDE: 'Aide sociale', FOURNISSEUR: 'Fournisseur', REMBOURSEMENT_SALARIE: 'Restitution à un salarié', VIREMENT_RETENUES: 'Virement des retenues',
  SUBVENTION: 'Subvention', CREANCE: 'Remboursement d\'une créance', AUTRE: 'Autre',
};
export const STATUTS_RETENUE = {
  PROPOSE: ['alerte', 'Proposée'], VALIDE: ['info', 'Validée'], TRANSMIS: ['info', 'Transmise'], CONSOMME: ['succes', 'Retenue'], PARTIEL: ['alerte', 'Partielle'],
  NON_CONSOMME: ['danger', 'Non retenue'], CLOS: ['neutre', 'Close'], ANNULE: ['neutre', 'Annulée'],
};
export const VERDICTS = { ADMISSIBLE: ['succes', 'Admissible'], BLOQUE: ['danger', 'Bloquée'] };
export const STATUTS_LOT = { OUVERT: ['info', 'Ouvert'], ARRET_DEMANDE: ['alerte', 'Arrêt demandé'], ARRETE: ['succes', 'Arrêté'], TRANSMIS: ['info', 'Transmis'], CLOS: ['neutre', 'Clos'] };
export const MOTIFS_BLOCAGE = {
  TRANSMISSION_DESACTIVEE: 'Prélèvement pas encore activé (mode vérification)', FONDEMENT_ABSENT: 'Fondement juridique de la retenue absent ou non valide',
  CONSENTEMENT_ABSENT: 'Consentement du salarié absent', SALARIE_INACTIF: 'Salarié inactif sur la période', SORTIE: 'Sortie du salarié signalée',
  CREANCE_SIMULATION: 'Créance de simulation : jamais prélevée', CREANCE_NON_VERIFIABLE: 'Créance introuvable ou non vérifiable',
  CREANCE_SUSPENDUE: 'Créance suspendue ou hors retenue', LITIGE: 'Créance en litige', DEPASSEMENT_SOLDE: 'Montant supérieur au solde restant dû',
  RUBRIQUE_ABSENTE: 'Rubrique de Paie absente pour ce type de retenue', PERIODE_CLOSE: 'Période close ou écoulée', ASSIETTE_ABSENTE: 'Dossier salarial validé absent (assiette inconnue)',
};
export const NATURES_RETENUE = { ECHEANCE: 'Échéance', RAPPEL_RELIQUAT: 'Rappel de reliquat', REMBOURSEMENT_ANTICIPE: 'Remboursement anticipé', REGULARISATION: 'Régularisation' };
export const ETATS_SIMULES = { CONSOMME: ['succes', 'Retenue entière'], PARTIEL: ['alerte', 'Retenue partielle'], NON_CONSOMME: ['danger', 'Rien de retenu'], EXCLU: ['neutre', 'Exclue'] };
export const ETATS_ECRITURE = { BROUILLON: ['alerte', 'Brouillon'], VALIDEE: ['succes', 'Validée'], ABANDONNEE: ['neutre', 'Abandonnée'] };
export const ETATS_PERIODE = { OUVERTE: ['info', 'Ouverte'], VALIDEE: ['alerte', 'Clôture demandée'], CLOSE: ['neutre', 'Close'] };
export const JOURNAUX = { BQ: 'Banque', CA: 'Caisse', PA: 'Paie (retenues)', OD: 'Opérations diverses' };
export const ACTES_PRET = {
  REMBOURSEMENT_ANTICIPE: 'Remboursement anticipé', SUSPENSION: 'Suspension des échéances', REPRISE: 'Reprise des échéances', REPORT: 'Report d\'échéances',
  REGULARISATION: 'Régularisation',
};
export const ACTIONS_HISTORIQUE = {
  DEPOSER: 'Dépôt', SAISIR: 'Saisie au guichet', PRISE_EN_CHARGE: 'Prise en charge', RECEVABLE: 'Déclarée recevable', IRRECEVABLE: 'Déclarée irrecevable',
  COMPLEMENT: 'Complément demandé', ACCORDER: 'Accord', ACCORDER_MODIFIE: 'Accord modifié', REFUSER: 'Refus', PREPARER: 'Préparation', PREPARER_DECAISSEMENT: 'Décaissement préparé',
  DECAISSER: 'Décaissement', ENCAISSER_REMBOURSEMENT_ANTICIPE: 'Remboursement anticipé encaissé', ANNULER: 'Annulation', PROPOSER: 'Proposition', VALIDER: 'Validation', REJETER: 'Rejet', SIGNER: 'Signature', SECOND_SIGNER: 'Seconde signature',
  EXECUTER: 'Exécution', VERIFIER: 'Vérification des pièces', VERSER: 'Versement constaté', CONSTITUER: 'Constitution', APPLIQUER: 'Application',
  AJOUTER_PIECE: 'Pièce ajoutée', DEMANDER_CLOTURE: 'Clôture demandée', CLORE: 'Clôture', ABANDONNER: 'Abandon', CONSTATER_VERSEMENT: 'Versement constaté',
  CONSTITUER_COMPLEMENT: 'Complément de créance', CONTROLER: 'Contrôle', CREER: 'Création', DEMANDER_COMPLEMENT: 'Complément demandé', ETABLIR: 'Établissement',
  METTRE_EN_ATTENTE_BUDGET: 'Mise en attente de budget', PROPOSER_CONTREPASSATION: 'Contrepassation proposée', VERIFIER_CONFORMES: 'Pièces vérifiées conformes',
};
/** Libellé d'une action d'historique (y compris « VALIDER_SUSPENSION », « REJETER_REPORT »…). */
export function libelleAction(code) {
  if (!code) return '';
  if (ACTIONS_HISTORIQUE[code]) return ACTIONS_HISTORIQUE[code];
  const m = /^(VALIDER|REJETER|PROPOSER)_(.+)$/.exec(code);
  if (m && ACTES_PRET[m[2]]) return `${ACTIONS_HISTORIQUE[m[1]]} : ${ACTES_PRET[m[2]].toLowerCase()}`;
  const t = code.toLowerCase().replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
}
export const AVIS_INSTRUCTION = {
  PRISE_EN_CHARGE: 'Prendre en charge (mise à l\'étude)', RECEVABLE: 'Recevable : transmettre pour décision', COMPLEMENT: 'Demander un complément au salarié',
  IRRECEVABLE: 'Irrecevable',
};
export const TYPES_PIECE_AIDE = { JUSTIFICATIF: 'Justificatif', FACTURE: 'Facture', DECISION: 'Décision', AUTRE: 'Autre' };
export const COUPURES = [10000, 5000, 2000, 1000, 500, 250, 200, 100, 50, 25, 10, 5, 1];
